import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useParams, useNavigate } from "react-router"
import axios from 'axios';

import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

import { AddableCourse, CourseLite, CourseOption } from '../../types/course.response';
import { MutationEnvelope } from '../../types/mutation.response';
import { programsApi } from '../../services/api/program.api';
import { userCoursesApi } from '../../services/api/user-courses.api';
import { userExamsApi } from '../../services/api/user-exams.api';
import ProgramHeader from './ProgramHeader';
import ProgramTabs, { useProgramView } from './ProgramTabs';
import InfoSidebar from './ProgramInfoSidebar';
import RequirementSection from '../../components/requirement-section/RequirementSection';
import RequirementSectionPlaceholder from '../../components/requirement-section/RequirementSectionPlaceholder';
import RequirementOverlay from '../../components/requirement-overlay/RequirementOverlay';
import ChecklistView from '../../components/checklist/ChecklistView';
import ProgramPageSkeleton from '../../components/loading/ProgramPageSkeleton';
import { CourseRequirementOverride } from '../../services/api/user-courses.api';
import { useCourseActions } from '../../hooks/useCourseActions';
import { usePinnedSidebar } from '../../hooks/usePinnedSidebar';
import { useSlicePrefetch } from '../../hooks/useSlicePrefetch';
import { programSlices } from '../../services/programSlices';
import { reconcileUserItems } from '../../services/userCourseCache';
import { ensureSliceCacheIdentity } from '../../services/sliceCacheIdentity';
import { isRequirementFulfilled } from '../../utils/requirementResponseUtils';
import ChevronIcon from '../../components/icons/ChevronIcon';

/** skeleton 一到就发的首批切片数 = 编排器一个批次;其余由视口预取驱动 */
const FIRST_BATCH_SIZE = 3;

export default function Program() {
    const { programId } = useParams();
    const navigate = useNavigate();
    const [view] = useProgramView();
    const[error, setError] = useState<string | undefined>(undefined);
    const[userCourses, setUserCourses] = useState<CourseOption[]>([]);
    const[optimisticFavorites, setOptimisticFavorites] = useState<Map<string, boolean>>(new Map());
    const[showSatisfied, setShowSatisfied] = useState(false);
    const[openRequirement, setOpenRequirement] = useState<{ programId: string; requirementId: string } | null>(null);

    const { isLoggedIn, authLoading, user } = useAuth();
    const { openLoginModal } = useModal();

    // program 数据由切片编排器持有(Home / overlay 共享缓存),订阅版本号派生快照
    const sliceVersion = useSyncExternalStore(programSlices.subscribe, programSlices.getVersion);
    void sliceVersion;
    const snap = programId ? programSlices.getSnapshot(programId) : undefined;
    const program = snap?.status === 'loaded' ? snap.program : undefined;

    // stateVersion 落地前的乱序保护:只应用最新一次发起的刷新结果
    //(stateVersion 在场时由编排器 applyFulfillment 内部兜底)
    const refreshSeqRef = useRef(0);

    // 改课/改 override 后的轻量刷新:fulfillment patch 打在编排器的合并对象上。
    // 也承担 userCourses 的首次拉取;skeleton 尚未加载时只拉用户课程
    //(skeleton 到达时自带最新 fulfillment)。
    // outcome(契约 §4):内嵌详情档在场时直接用,省掉 GET fulfillment;
    // meta 在场且本 program 不受影响时跳过 fulfillment 拉取,只刷用户课程。
    const refreshFulfillment = async (outcome?: MutationEnvelope) => {
        if (!isLoggedIn || !programId) return;
        const seq = ++refreshSeqRef.current;
        try {
            const loaded = programSlices.getSnapshot(programId)?.status === 'loaded';
            const embedded = outcome?.fulfillments?.find(f => f.programId === programId);
            const affected = !outcome?.meta || outcome.meta.affectedProgramIds.includes(programId);
            const [courses, exams, fulfillment] = await Promise.all([
                userCoursesApi.getCourses(),
                userExamsApi.getExams(),
                embedded ?? (loaded && affected
                    ? programsApi.getProgramFulfillment(programId)
                    : Promise.resolve(null)),
            ]);
            if (seq !== refreshSeqRef.current) return; // 已有更新的刷新,丢弃
            const userItems = [...courses, ...exams];
            // §3.4-c 双读对拍:userStates 覆盖全部用户课,任一详情档都可作基准
            const reference = fulfillment ?? outcome?.fulfillments?.[0];
            if (reference) reconcileUserItems(reference.userStates, userItems);
            if (fulfillment) {
                programSlices.applyFulfillment(programId, fulfillment, userItems);
            }
            // 内嵌档可能还带了其他已加载 program(Home 缓存共享),顺手打上
            outcome?.fulfillments?.forEach(f => {
                if (f.programId !== programId) programSlices.applyFulfillment(f.programId, f, userItems);
            });
            setUserCourses(courses);
        } catch (error) {
            if (seq !== refreshSeqRef.current) return;
            console.error('Error refreshing fulfillment:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
        }
    };

    // skeleton-first:挂载时确保 skeleton 已加载/在途;缓存命中(跨页返回)时
    // 用轻量 fulfillment 兜住其他页面改课造成的过期
    useEffect(() => {
        if (authLoading || !programId) return;
        ensureSliceCacheIdentity(user ? `user:${user.id}` : 'guest');
        programSlices.ensureProgram(programId);
        if (isLoggedIn) {
            refreshFulfillment();
        } else {
            setUserCourses([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user, programId]);

    // skeleton 一到就发首批切片;checklist 视图全量入队(紧凑清单,期望完整状态)。
    // requirements 视图的后续批次由占位区块的视口预取驱动
    useEffect(() => {
        if (!programId) return;
        const current = programSlices.getSnapshot(programId);
        if (current?.status !== 'loaded' || !current.program) return;
        if (view === 'checklist') {
            programSlices.requestSlices(programId, current.program.requirements.map(r => r.info.id));
            return;
        }
        const firstBatch = current.program.requirements
            .filter(r => !isRequirementFulfilled(r))
            .slice(0, FIRST_BATCH_SIZE)
            .map(r => r.info.id)
            .filter(id => current.sliceStatus[id] === 'idle');
        if (firstBatch.length > 0) {
            programSlices.requestSlices(programId, firstBatch);
        }
    }, [programId, view, sliceVersion]);

    // 占位区块的视口预取(root = 浏览器视口)
    const registerPlaceholder = useSlicePrefetch();

    const { sidebarContainerRef, sidebarContentRef, containerStyle, contentClassName, contentStyle } = usePinnedSidebar({
        deps: [program, view, showSatisfied],
    });

    const courseActions = useCourseActions({
        isLoggedIn,
        openLoginModal,
        userCourses,
        optimisticFavorites,
        setOptimisticFavorites,
        onSuccess: refreshFulfillment,
        fulfillmentProgramIds: () => programSlices.getLoadedProgramIds(),
        onError: (err, action) => {
            if (action === 'favorite') {
                if (axios.isAxiosError(err) && err.response?.status === 401) {
                    setError('Please log in to save favorites');
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to update favorite status');
                }
            } else {
                setError(err instanceof Error ? err.message : 'An error occurred');
            }
        },
    });

    // 刷新由 onSuccess(refreshFulfillment)承担,组件只需 Promise<void>,吞掉 outcome
    const handleAddCourse = async (
        course: AddableCourse,
        overrides: CourseRequirementOverride[],
        semester: string | null,
        classSectionIds?: number[],
    ) => {
        setError(undefined);
        await courseActions.handleAddCourse(course, overrides, semester, classSectionIds);
    };

    const handleDeleteCourse = async (course: CourseLite) => {
        setError(undefined);
        await courseActions.handleDeleteCourse(course);
    };

    const handleFavoriteCourse = courseActions.handleFavoriteCourse;

    const handleOverride = async (course: CourseLite, override: CourseRequirementOverride) => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        const userItemId = course.userItemId;
        if (!userItemId) return;

        setError(undefined);
        try {
            const fulfillmentIds = programSlices.getLoadedProgramIds();
            const outcome = course.type === 'EXAM'
                ? await userExamsApi.addOverride(userItemId, override, fulfillmentIds)
                : await userCoursesApi.addOverride(userItemId, override, fulfillmentIds);
            await refreshFulfillment(outcome);
        } catch (error) {
            console.error('Error updating override:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
        }
    };

    const renderRequirementBlock = (requirement: NonNullable<typeof program>['requirements'][number]) => (
        <div key={requirement.info.id} className="bg-white">
            {snap!.sliceStatus[requirement.info.id] === 'loaded' ? (
                <RequirementSection
                    program={program!}
                    requirement={requirement}
                    handleAddCourse={handleAddCourse}
                    handleDeleteCourse={handleDeleteCourse}
                    handleFavoriteCourse={handleFavoriteCourse}
                    handleOverride={handleOverride}
                    optimisticFavorites={optimisticFavorites}
                    userCourses={userCourses}
                    onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                />
            ) : (
                <RequirementSectionPlaceholder
                    program={program!}
                    requirement={requirement}
                    handleAddCourse={handleAddCourse}
                    handleDeleteCourse={handleDeleteCourse}
                    handleOverride={handleOverride}
                    onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                    onRegister={registerPlaceholder}
                />
            )}
        </div>
    );

    const isInitialLoading = !authLoading && !!programId && (!snap || snap.status === 'loading');
    const loadError = snap?.status === 'error' ? (snap.error ?? 'Failed to load') : undefined;

    return (
        <>
        <div className="mt-2 relative">
            {isInitialLoading && <ProgramPageSkeleton />}
            {loadError && (
                <div className="bg-red-50 p-4 rounded mb-6 border border-red-100 max-w-[1200px] mx-auto mt-8 px-4 md:px-12">
                    <p className="text-red-700 font-medium">{loadError}</p>
                    <button
                        className="mt-3 text-sm text-red-700 underline"
                        onClick={() => programSlices.ensureProgram(programId!)}
                    >
                        Try again
                    </button>
                </div>
            )}
            {!loadError && error && (
                <div className="bg-red-50 p-4 rounded mb-6 border border-red-100 max-w-[1200px] mx-auto mt-8 px-4 md:px-12">
                    <p className="text-red-700 font-medium">{error}</p>
                </div>
            )}
            {program && (
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12">
                    {/* Top Banner */}
                    <div className="w-full h-32 bg-gradient-to-r from-blue-100 to-blue-200 shrink-0 rounded-xl" />

                    <div>
                        <ProgramHeader program={program} />
                        <ProgramTabs />
                        {/* Two Column Layout */}
                        <div className="pt-6 grid grid-cols-1 md:grid-cols-[1fr_312px] gap-6 items-start">
                            {/* Left Column: Main Content */}
                            <div className="w-full min-w-0 pb-20">
                                {view === "checklist" ? (
                                    <ChecklistView
                                        program={program}
                                        handleAddCourse={handleAddCourse}
                                        handleDeleteCourse={handleDeleteCourse}
                                        handleOverride={handleOverride}
                                    />
                                ) : (
                                    <>
                                        {program.requirements.filter(r => !isRequirementFulfilled(r)).map(renderRequirementBlock)}

                                        {program.requirements.filter(r => isRequirementFulfilled(r)).length > 0 && (
                                            <div>
                                                <button
                                                    onClick={() => setShowSatisfied(!showSatisfied)}
                                                    className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors pb-6"
                                                >
                                                    <ChevronIcon
                                                        direction={showSatisfied ? "down" : "right"}
                                                        className="w-4 h-4"
                                                    />
                                                    Completed Requirements ({program.requirements.filter(r => isRequirementFulfilled(r)).length})
                                                </button>

                                                {showSatisfied && (
                                                    <div>
                                                        {program.requirements.filter(r => isRequirementFulfilled(r)).map(renderRequirementBlock)}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div
                                ref={sidebarContainerRef}
                                className="hidden md:block self-start"
                                style={containerStyle}
                            >
                                <div
                                    ref={sidebarContentRef}
                                    className={contentClassName}
                                    style={contentStyle}
                                >
                                    <InfoSidebar
                                        program={program}
                                        onProgramUpdate={async () => { programSlices.invalidateProgram(programId!); }}
                                        onPoolClick={(poolKey) => navigate(`/programs/${programId}/pools/${encodeURIComponent(poolKey)}`, { state: { from: 'program' } })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        {openRequirement && (
            <RequirementOverlay
                programId={openRequirement.programId}
                requirementId={openRequirement.requirementId}
                onClose={() => setOpenRequirement(null)}
                onDataChanged={() => refreshFulfillment()}
                userCourses={userCourses}
            />
        )}
        </>
    );
}
